import { differenceBy, partition } from 'lodash-es';

import { JobPosition, JobTitle } from '@pflegia/data-access';
import { B_PRIORITIES, CandidatePriority, D_PRIORITIES } from '@pflegia/types/api/candidate/candidate-priority';
import { CaregiverQualification } from '@pflegia/types/api/candidate/care-giver-qualification';
import { NursingApprenticeshipExperience } from '@pflegia/types/api/candidate/nursing-apprenticeship-experience';
import { QUALIFICATION_TO_RECOGNITION, QUESTIONS_WITH_QUALIFICATION_RECOGNITION } from '@pflegia/types/api/candidate/recognition';
import { YearsOfExperience } from '@pflegia/types/api/candidate/years-of-experience';
import { CareType } from '@pflegia/types/api/employer/care-type';
import { FunnelQuestion, FunnelQuestionCategory } from '@pflegia/types/api/funnel/questions';
import { ConfirmationStatus } from '@pflegia/types/api/general/confirmation-status';
import { DowngradeFn, JobTitleCategory, RequiredJobTitle } from '@pflegia/types/api/job-title';
import { COMMON_DOWNGRADES, JobTitleEnum } from '@pflegia/types/api/job-title/job-titles.enum';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { ConditionalStep, LocalFunnelStep } from '@pflegia/ui/funnel/types';
import { getSelectedJobTitle } from '@pflegia/ui/funnel/utils/job-titles';
import { FunnelAnswers } from '@pflegia/ui/pflegia/funnel/types/funnel-answers';
import { assertIsDefined } from '@pflegia/util/functions/type-guards';
import { getMainPriorityJobTitle } from '@pflegia/util/pflegia/job-title';

import { isPhysiotherapyWithPraxismanagerWithNoQualification } from '../helpers/is-physiotherapy-with-praxismanager-with-no-qualification';
import { findDowngradedJobTitles } from '../helpers/multiposting/find-downgraded-job-titles';
import { getFunnelPropsData } from '../helpers/multiposting/get-funnel-props-data';
import { FunnelProps } from '../hooks/use-funnel-state';
import { isMedicalStaffFlow } from './utils';

const KEY = FunnelQuestion.YearsOfExperience;
type YearsOfExperienceStep = LocalFunnelStep<
  JobTitle,
  FunnelAnswers,
  FunnelQuestion.YearsOfExperience,
  { jobPosition: JobPosition; sourceDomain: SourceDomain }
>;

type YearsOfExperienceDowngradeFn = DowngradeFn<
  YearsOfExperience,
  { employerCareType: CareType; isPraxia: boolean; jobQualification: ConfirmationStatus | CaregiverQualification }
>;

export const yearsOfExperienceStep: ConditionalStep<YearsOfExperienceStep, { isMultiposting?: boolean }> = ({ isMultiposting } = {}) => ({
  category: FunnelQuestionCategory.EducationAndTraining,
  condition: answers => {
    if (isMedicalStaffFlow(answers)) return false;
    const selectedJobTitle = getSelectedJobTitle<JobTitle>(answers, KEY);
    const isVisible = [CandidatePriority.A, ...B_PRIORITIES, CandidatePriority.C, ...D_PRIORITIES].includes(
      selectedJobTitle.trackingPriority as CandidatePriority,
    );
    if (!isVisible) return false;

    // Do not show if any of the questions with qualification recognition is acquired abroad
    // and qualification recognition is not answered yet
    return !QUESTIONS_WITH_QUALIFICATION_RECOGNITION.some(
      question => answers[question] === ConfirmationStatus.AcquiredAbroad && !answers[QUALIFICATION_TO_RECOGNITION[question]]?.status,
    );
  },
  getNewJobTitle: isMultiposting ? multipostingDowngrade : regularFunnelDowngrade,
  key: KEY,
});

const regularFunnelDowngrade: YearsOfExperienceStep['getNewJobTitle'] = (answer, answers, jobTitles) => {
  if (answer !== YearsOfExperience.None) return null;

  const selectedJobTitle = getSelectedJobTitle<JobTitle>(answers, KEY);
  if (isPhysiotherapyWithPraxismanagerWithNoQualification(answers)) {
    const physicalTherapist = jobTitles.find(jt => jt.title === JobTitleEnum.PhysicalTherapist);
    assertIsDefined(physicalTherapist, 'Physical therapist');
    return physicalTherapist;
  }

  const careerChangers = jobTitles.find(jobTitle => jobTitle.title === JobTitleEnum.CareerChangers);
  assertIsDefined(careerChangers, 'Career changers');

  const caregiverQualificationAnswer = answers[FunnelQuestion.CaregiverQualification];
  if (caregiverQualificationAnswer === CaregiverQualification.NoTraining) return careerChangers;

  const naeAnswer = answers[FunnelQuestion.DQualification];
  if (naeAnswer !== NursingApprenticeshipExperience.NoApprenticeship) return null;

  if (!D_PRIORITIES.includes(selectedJobTitle.trackingPriority as CandidatePriority)) return null;

  return careerChangers;
};

const multipostingDowngrade: YearsOfExperienceStep['getNewJobTitle'] = (answer, answers, jobTitles, props) => {
  const jobQualification = answers[FunnelQuestion.JobQualification] as ConfirmationStatus | CaregiverQualification;
  if (jobQualification === ConfirmationStatus.Yes) return null;

  const jobTitlesAnswers = answers[FunnelQuestion.PositionDesired];
  assertIsDefined(jobTitlesAnswers, 'Multiposting Job Position');

  const [originalJobTitles, downgradedJobTitles] = partition(
    jobTitlesAnswers,
    ({ question }) => question === FunnelQuestion.PositionDesired,
  ).map(jtAnswers => jtAnswers.map(({ jobTitle }) => jobTitle)) as RequiredJobTitle[][];
  assertIsDefined(originalJobTitles, 'Selected job titles');

  const mainPriorityJobTitle = getMainPriorityJobTitle(originalJobTitles);
  assertIsDefined(mainPriorityJobTitle, 'Main priority job title');

  const { employerCareType, isPraxia } = getFunnelPropsData(props as FunnelProps);
  const downgradeResult = getDowngradedJobTitle(mainPriorityJobTitle, answer, { jobQualification, employerCareType, isPraxia });
  const newJobTitles = findDowngradedJobTitles(downgradeResult, jobTitles, isPraxia);
  if (
    !differenceBy(newJobTitles, downgradedJobTitles, [mainPriorityJobTitle.jobTitle, ...mainPriorityJobTitle.similarJobTitles], 'title')
      .length
  )
    return null; // means that the downgrade already was done before or we have downgraded to the same job title
  return newJobTitles;
};

const getDowngradedJobTitle: YearsOfExperienceDowngradeFn = (mainPriorityJobTitle, answer, props) => {
  switch (mainPriorityJobTitle.jobTitle?.trackingPriority) {
    case CandidatePriority.A:
      return getDowngradePathForA(mainPriorityJobTitle, answer, props);
    case CandidatePriority.B:
    case CandidatePriority.B2:
    case CandidatePriority.B3:
    case CandidatePriority.B4:
    case CandidatePriority.B5:
    case CandidatePriority.B6:
      return getDowngradePathForB(mainPriorityJobTitle, answer, props);
    case CandidatePriority.C:
      return getDowngradePathForC(mainPriorityJobTitle, answer, props);
    case CandidatePriority.D2:
      return getDowngradePathForD(mainPriorityJobTitle, answer, props);
  }
  return null;
};

const getDowngradePathForA: YearsOfExperienceDowngradeFn = ({ category }, answer, { jobQualification, employerCareType, isPraxia }) => {
  switch (category) {
    case JobTitleCategory.Praxia: {
      if (jobQualification !== ConfirmationStatus.No) return null;
      switch (employerCareType) {
        case CareType.DentalPractice:
          return [JobTitleEnum.QualifiedDentalEmployee];
        case CareType.Physiotherapy:
          return [JobTitleEnum.PhysicalTherapist];
        default:
          return [JobTitleEnum.MedicalAssistant];
      }
    }
    case JobTitleCategory.Standard: {
      switch (jobQualification) {
        case ConfirmationStatus.No:
          if (answer === YearsOfExperience.None) return [JobTitleEnum.CareerChangers];
          return isPraxia ? [JobTitleEnum.MedicalAssistance] : COMMON_DOWNGRADES.Nursing.Assistants;
        case ConfirmationStatus.InProgress:
          return isPraxia ? COMMON_DOWNGRADES.Medical.Assistants : COMMON_DOWNGRADES.Nursing.CareTakers;
      }
    }
  }
  return null;
};
const getDowngradePathForB: YearsOfExperienceDowngradeFn = ({ jobTitle }, answer, { isPraxia, jobQualification }) => {
  const { trackingPriority } = jobTitle;
  if (jobQualification !== ConfirmationStatus.No) return null;
  switch (trackingPriority) {
    case CandidatePriority.B: {
      if (answer === YearsOfExperience.None) return [JobTitleEnum.CareerChangers];
      return isPraxia ? [JobTitleEnum.MedicalAssistance] : COMMON_DOWNGRADES.Nursing.Assistants;
    }
    case CandidatePriority.B2:
    case CandidatePriority.B3:
    case CandidatePriority.B4:
    case CandidatePriority.B5:
    case CandidatePriority.B6:
      return [JobTitleEnum.MedicalAssistance];
  }
  return null;
};

const getDowngradePathForC: YearsOfExperienceDowngradeFn = (mainPriorityJobTitle, answer, { jobQualification }) => {
  if (mainPriorityJobTitle.category === JobTitleCategory.Companion) {
    switch (jobQualification) {
      case CaregiverQualification.NoTraining:
        return answer === YearsOfExperience.None ? [JobTitleEnum.CareerChangers] : null;
      case CaregiverQualification.Searching:
        return [JobTitleEnum.Apprenticeship];
      default:
        return [JobTitleEnum.Companion];
    }
  }

  if (jobQualification === ConfirmationStatus.InProgress) {
    return null;
  }

  return answer === YearsOfExperience.None ? [JobTitleEnum.CareerChangers] : [JobTitleEnum.AssistantNurse];
};

const getDowngradePathForD: YearsOfExperienceDowngradeFn = (mainPriorityJobTitle, answer, { jobQualification }) => {
  if (
    (mainPriorityJobTitle.category === JobTitleCategory.Praxia && answer === YearsOfExperience.None) ||
    (jobQualification === ConfirmationStatus.No && answer === YearsOfExperience.None)
  )
    return [JobTitleEnum.CareerChangers];
  return null;
};
